import {
  LedgerType,
  BudgetSearchResultRecord,
  ProposalSearchResultRecord,
  WorksheetSearchResultRecord,
  AdjustmentLineSearchResultRecord,
  WorkforceSearchResultRecord,
  StorySearchResultRecord,
  PublicationSearchResultRecord,
  ClassicReportSearchResultRecord,
  NextGenCustomReportSearchResultRecord,
  NextGenStandardReportSearchResultRecord,
} from "../SearchResultRow/SearchResultRow.types";

import { getEncodedBudgetId, getEncodedProposalId, getEncodedWorksheetId } from "./encodeAndDecode.utils";

export const getBudgetRecordUrl = ({ subdomain, budgetId }: BudgetSearchResultRecord) =>
  `/budgets/${subdomain}/${getEncodedBudgetId(budgetId)}`;

export const getProposalRecordUrl = ({ subdomain, budgetId, proposalId }: ProposalSearchResultRecord) => {
  const encodedBudgetId = getEncodedBudgetId(budgetId);
  const encodedProposalId = getEncodedProposalId(budgetId, proposalId);
  return `/budgets/${subdomain}/${encodedBudgetId}/proposals/${encodedProposalId}/details`;
};

export const getWorksheetRecordUrl = ({
  subdomain,
  budgetId,
  proposalId,
  worksheetId,
  ledgerType,
  hasLinkedWorkforce,
}: Pick<
  WorksheetSearchResultRecord,
  "subdomain" | "budgetId" | "proposalId" | "worksheetId" | "hasLinkedWorkforce" | "ledgerType"
>) => {
  const encodedBudgetId = getEncodedBudgetId(budgetId);
  const encodedProposalId = getEncodedProposalId(budgetId, proposalId);
  const encodedWorksheetId = getEncodedWorksheetId(budgetId, proposalId, worksheetId);
  if (hasLinkedWorkforce && ledgerType === LedgerType.Revenues) {
    return `/budgets/${subdomain}/${encodedBudgetId}/proposals/${encodedProposalId}/worksheets/${encodedWorksheetId}`;
  }
  if (hasLinkedWorkforce) {
    return `/budgets/${subdomain}/${encodedBudgetId}/proposals/${encodedProposalId}/worksheets/${encodedWorksheetId}/adjustments`;
  }
  return `/budgets/${subdomain}/${encodedBudgetId}/proposals/${encodedProposalId}/operations/${ledgerType.toLowerCase()}/${encodedWorksheetId}`;
};

export const getAdjustmentLineRecordUrl = ({
  subdomain,
  budgetId,
  proposalId,
  worksheetId,
  ledgerType,
  hasLinkedWorkforce,
  displayAccountString,
}: AdjustmentLineSearchResultRecord) => {
  const worksheetUrl = getWorksheetRecordUrl({
    subdomain,
    budgetId,
    proposalId,
    worksheetId,
    ledgerType,
    hasLinkedWorkforce,
  });
  return `${worksheetUrl}?filterBy=displayAccountString&filterTerm=${displayAccountString}`;
};

export const getWorkforceRecordUrl = ({
  workforceId,
  subdomain,
  positionGroup,
  startDate,
  endDate,
}: WorkforceSearchResultRecord) =>
  `/data_manager/${subdomain}/workforce/workforces/${workforceId}/positionGroups/${positionGroup}/forecast?start=${startDate}&end=${endDate}`;

export const getStoryRecordUrl = ({ subdomain, storyId, publicationId }: StorySearchResultRecord) => {
  if (publicationId) {
    return `/stories/${subdomain}/publication/${publicationId}/story/${storyId}`;
  }
  return `/stories/${subdomain}/story/${storyId}`;
};

export const getPublicationRecordUrl = ({ subdomain, publicationId }: PublicationSearchResultRecord) =>
  `/stories/${subdomain}/publications/${publicationId}/stories`;

export const getClassicReportRecordUrl = ({ subdomain, classicReportId }: ClassicReportSearchResultRecord) =>
  `/reports#/${subdomain}/${classicReportId}`;

export const getNextGenCustomReportRecordUrl = ({
  subdomain,
  nextGenReportId,
}: NextGenCustomReportSearchResultRecord) => `/reporting/${subdomain}/${nextGenReportId}/views`;

export const getNextGenStandardReportRecordUrl = ({
  subdomain,
  standardReportId,
  lensId,
}: NextGenStandardReportSearchResultRecord) => `/reporting/${subdomain}/standard/${standardReportId}?lensId=${lensId}`;
